import {
  getBiggestThumbnailUrl,
  getCategoryImageUrl,
  getMainImageUrl,
  getTranslatedProperty,
  isLandingPage,
  isProduct,
} from "@shopware/helpers";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import type { Schemas } from "#shopware";

export type UseCmsHeadReturn = ReturnType<typeof useCmsHead>;

type CmsPageEntity =
  | Schemas["Category"]
  | Schemas["LandingPage"]
  | Schemas["Product"];

type HeadMetaTag = {
  name?: string;
  property?: string;
  content: string;
};

function resolveCanonicalUrl(
  baseUrl: string,
  route: RouteLocationNormalizedLoaded,
): string {
  const normalizedBase = baseUrl.replace(/\/$/, "");
  const pathWithoutQuery = route.fullPath.split("?")[0];
  const hasQueryParams = route.fullPath.includes("?");
  const routeName = route.name?.toString() || "";
  const historyRouteName =
    (import.meta.client && window.history?.state?.routeName) || "";

  if (
    pathWithoutQuery === "/search" ||
    pathWithoutQuery.startsWith("/search")
  ) {
    return `${normalizedBase}${route.fullPath}`;
  }

  const isListingPageFromRouteName =
    routeName === "frontend.navigation.page" ||
    routeName.includes("navigation") ||
    historyRouteName === "frontend.navigation.page" ||
    historyRouteName.includes("navigation");

  if (isListingPageFromRouteName) {
    return `${normalizedBase}${route.fullPath}`;
  }

  if (hasQueryParams && !isListingPageFromRouteName) {
    const excludedPaths = [
      "/checkout",
      "/account",
      "/login",
      "/register",
      "/wishlist",
      "/product",
    ];
    const isExcluded = excludedPaths.some((excluded) =>
      pathWithoutQuery.startsWith(excluded),
    );

    if (!isExcluded && pathWithoutQuery !== "/") {
      return `${normalizedBase}${route.fullPath}`;
    }
  }

  return `${normalizedBase}${pathWithoutQuery}`;
}

function sanitizeMetaDescription(text: string | undefined | null): string {
  if (!text || typeof text !== "string") return "";
  let plain = text.replace(/(<([^>]+)>)/gi, " ");
  plain = plain
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
  return plain;
}

function buildDocumentTitle(pageName: string, brandSuffix?: string): string {
  if (!pageName) return brandSuffix ?? "";
  if (!brandSuffix) return pageName;
  return `${pageName} | ${brandSuffix}`;
}

/** Prefer full/cover URL; fall back to largest thumbnail. Always return an absolute URL when possible. */
function resolveProductCoverImageUrl(
  product: Schemas["Product"],
  baseUrl: string,
): string {
  const coverMedia = product.cover?.media;
  const imageUrl =
    coverMedia?.url ||
    getBiggestThumbnailUrl(coverMedia) ||
    getMainImageUrl(product) ||
    "";

  if (!imageUrl) return "";
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;

  const normalizedBase = baseUrl.replace(/\/$/, "");
  if (!normalizedBase) return imageUrl;
  return imageUrl.startsWith("/")
    ? `${normalizedBase}${imageUrl}`
    : `${normalizedBase}/${imageUrl}`;
}

export function useCmsHead(
  entity: Ref<CmsPageEntity> | ComputedRef<CmsPageEntity>,
  options?: {
    mainShopTitle?: string;
  },
): void {
  const config = useRuntimeConfig();
  const router = useRouter();
  const entityData = computed(() => unref(entity) as CmsPageEntity);

  const getBaseUrl = (): string => {
    return (
      config.public.shopware?.devStorefrontUrl ||
      (config.shopware as { devStorefrontUrl?: string })?.devStorefrontUrl ||
      ""
    );
  };

  const pageName = computed(() => {
    const entity = entityData.value;
    if (!entity) return "";
    const metaTitle = getTranslatedProperty(entity, "metaTitle");
    return metaTitle || getTranslatedProperty(entity, "name");
  });

  const title = computed(() =>
    buildDocumentTitle(pageName.value, options?.mainShopTitle),
  );

  const metaDescription = computed(() => {
    const entity = entityData.value;
    if (!entity) return "";
    return sanitizeMetaDescription(
      getTranslatedProperty(entity, "metaDescription"),
    );
  });

  const canonicalUrl = computed(() => {
    const baseUrl = getBaseUrl();
    if (!baseUrl) return "";
    return resolveCanonicalUrl(baseUrl, router.currentRoute.value);
  });

  const enhancedMeta = computed(() => {
    const entity = entityData.value;
    if (!entity) return [];

    const tags: HeadMetaTag[] = [];
    const keywords = getTranslatedProperty(entity, "keywords");
    const description = metaDescription.value;

    if (keywords) {
      tags.push({ name: "keywords", content: keywords });
    }
    if (description) {
      tags.push({ name: "description", content: description });
    }

    const siteName =
      config.public.general?.name || "My Sofa Bed";

    if (title.value) {
      tags.push({ property: "og:title", content: title.value });
    }
    if (description) {
      tags.push({ property: "og:description", content: description });
    }
    if (canonicalUrl.value) {
      tags.push({ property: "og:url", content: canonicalUrl.value });
    }

    tags.push(
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: siteName },
    );

    if (!isLandingPage(entity)) {
      const imageContent = isProduct(entity)
        ? resolveProductCoverImageUrl(entity, getBaseUrl())
        : getCategoryImageUrl(entity as Schemas["Category"]);

      if (imageContent) {
        tags.push(
          { property: "og:image", content: imageContent },
          { name: "twitter:image", content: imageContent },
          { name: "twitter:card", content: "summary_large_image" },
        );
      }
    }

    return tags;
  });

  const canonicalLink = computed(() =>
    canonicalUrl.value ? [{ rel: "canonical", href: canonicalUrl.value }] : [],
  );

  useHead({
    title,
    meta: enhancedMeta,
    link: canonicalLink,
  }, { tagPriority: -8 });
}
